import { SearchRounded, VisibilityRounded } from "@mui/icons-material";
import {
  Box,
  CardActions,
  CardContent,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import MainLayout from "../../layouts/main/main_layout";
import RewardsLayout from "../../layouts/rewards/rewards_layout";
import Loading from "../../components/loading/loading";
import AccessDenied from "../../components/access_denied/access_denied";
import useUser from "../../providers/user/user_provider";
import { getRewardsList } from "../../services/rewards/rewards";
import { getMasterData } from "../../services/choices/choices_services";
import { RewardsListingModel } from "./model/rewards_listing";
import FilterRewards, { RewardsFilterValues } from "./component/filter_rewards/filter_rewards";
import SortRewardsPopup, {
  DEFAULT_REWARDS_SORT_OPTION,
  RewardsSortOption,
  rewardsSortOptionLabel,
  rewardsSortOptionToParams,
} from "./component/sort_rewards_popup/sort_rewards_popup";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../users/components/table_toolkit/table_toolkit";
import { ToolbarButton } from "../../components/toolbar_button/toolbar_button";
import RedemptionRequestsList from "./component/redemption_requests_list/redemption_requests_list";
import FilterIcon from "../../assets/filter_icon.svg";
import SortIcon from "../../assets/sort.svg";
import OrgSelector from "../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../store/use-selected-org-store";
import { black, greyEnabled, green, greenLight, purple } from "../../theme/colors";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

const EMPTY_FILTERS: RewardsFilterValues = {
  genderId: null,
  stateId: null,
  districtId: null,
  rewardFrom: null,
  rewardTo: null,
  ageFrom: null,
  ageTo: null,
  area: "",
  fromDate: "",
  toDate: "",
};

export default function Rewards() {
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentTab, setCurrentTab] = useState(
    searchParams.has("currentTab") ? +searchParams.get("currentTab")! : 0
  );

  const handleTabChange = (tab: number) => {
    setCurrentTab(tab);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("currentTab", tab.toString());
        return params;
      },
      { replace: true }
    );
  };

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<RewardsFilterValues>(EMPTY_FILTERS);
  const [sortOption, setSortOption] = useState<RewardsSortOption>(DEFAULT_REWARDS_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  const activeFilterCount = [
    filters.genderId !== null,
    filters.stateId !== null,
    filters.districtId !== null,
    filters.rewardFrom !== null && filters.rewardTo !== null,
    filters.ageFrom !== null && filters.ageTo !== null,
    !!filters.area,
    filters.fromDate && filters.toDate,
  ].filter(Boolean).length;

  const { data: masterDataList } = useQuery(["masterDataList"], async () => {
    let response = await getMasterData();
    return response.data.data;
  });

  useEffect(() => {
    const debounceTimeout = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(debounceTimeout);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, filters, selectedOrgId, sortOption, pageSize]);

  const { data: rewardsListData, isLoading, isError } = useQuery(
    ["rewardList", page, pageSize, debouncedSearchValue, filters, selectedOrgId, sortOption],
    async () => {
      const { sortBy, sortOrder } = rewardsSortOptionToParams(sortOption);
      let response = await getRewardsList({
        page,
        size: pageSize,
        keyword: debouncedSearchValue,
        rewardFrom: filters.rewardFrom,
        rewardTo: filters.rewardTo,
        startDate: filters.fromDate || null,
        endDate: filters.toDate || null,
        ageFrom: filters.ageFrom,
        ageTo: filters.ageTo,
        area: filters.area || null,
        genderId: filters.genderId,
        stateId: filters.stateId,
        district: filters.districtId,
        orgId: selectedOrgId,
        sortBy,
        sortOrder,
      });
      return response?.data?.data as { total: number; list: RewardsListingModel[] };
    }
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const count = rewardsListData?.total || 0;
  const list = rewardsListData?.list || [];

  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && list.length > 0;
  const showEmpty = !isLoading && !isError && list.length === 0;

  return (
    <MainLayout>
      {hasPermission("rewards", "read") ? (
        <RewardsLayout
          page={currentTab === 0 ? page : undefined}
          setPage={setPage}
          currentTab={currentTab}
          onTabChange={handleTabChange}
        >
          {currentTab === 1 ? (
            <RedemptionRequestsList />
          ) : (
            <>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
              mb={2}
            >
              <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
                <TextField
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="Search by User Name and contact number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <OrgSelector lockToOwnOrg />
                <ToolbarButton
                  icon={SortIcon}
                  label={rewardsSortOptionLabel(sortOption)}
                  onClick={() => setSortDialogOpen(true)}
                />
                <ToolbarButton
                  icon={FilterIcon}
                  label="Filters"
                  badgeCount={activeFilterCount}
                  onClick={() => setFilterDialogOpen(true)}
                />
              </Box>
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Loading />
              </Box>
            ) : isError ? (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                py={8}
                gap={0.5}
              >
                <Typography fontWeight={600} color={black}>
                  Something went wrong
                </Typography>
                <Typography variant="body2" color={greyEnabled}>
                  We couldn't load the rewards list. Please try again.
                </Typography>
              </Box>
            ) : showEmpty ? (
              <TableEmptyState
                message="No rewards found"
                hint="Try adjusting your search or filters."
              />
            ) : (
              <Box mt={1}>
                <TableHeaderRow
                  labels={[
                    { label: "Sl. No.", flex: 0.3 },
                    { label: "User", flex: 1.4 },
                    { label: "Date of Birth", flex: 0.8 },
                    { label: "Gender", flex: 0.6 },
                    { label: "Marital Status", flex: 0.8 },
                    { label: "State", flex: 0.8 },
                    { label: "District", flex: 0.8 },
                    { label: "Total Points", flex: 0.7 },
                    { label: "Redeemed Points", flex: 0.7 },
                    { label: "Available Points", flex: 0.7 },
                    { label: "Actions" },
                  ]}
                  lastColumnWidth={90}
                  align="left"
                  bordered
                />
                {list.map((item, index) => (
                  <TableRow key={item.id} bordered>
                    <TableCell label="Sl. No." flex={0.3} bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {(page - 1) * pageSize + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell label="User" flex={1.4} bordered>
                      <Typography fontWeight={600} color={black} noWrap>
                        {item.name || "-"}
                      </Typography>
                      <Typography variant="body2" color={greyEnabled} noWrap>
                        {item.mobileNo || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Date of Birth" flex={0.8} bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item.dob || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Gender" flex={0.6} bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item.gender || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Marital Status" flex={0.8} bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item.maritalStatus || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="State" flex={0.8} bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item.state || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="District" flex={0.8} bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item.district || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Total Points" flex={0.7} bordered>
                      <Typography fontSize={14} fontWeight={600} color={purple} noWrap>
                        {item.totalPoints ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Redeemed Points" flex={0.7} bordered>
                      <Typography fontSize={14} fontWeight={600} color={purple} noWrap>
                        {item.redeemedPoints ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Available Points" flex={0.7} bordered>
                      <Typography fontSize={14} fontWeight={600} color={purple} noWrap>
                        {item.availablePoints ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell isLast lastColumnWidth={90} bordered>
                      <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                        <Tooltip title="View details">
                          <IconButton
                            onClick={() => navigate(`/rewards/rewardsDetails/${item.userId}`)}
                            sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                          >
                            <VisibilityRounded sx={{ color: green, fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </Box>
            )}
          </CardContent>

          {showList && (
            <CardActions sx={{ px: 4, pb: 3 }}>
              <Box width="100%">
                <TableFooterPagination
                  count={count}
                  page={page - 1}
                  onPageChange={handleChangePage}
                  summaryText={customText}
                  rowsPerPage={pageSize}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  onRowsPerPageChange={setPageSize}
                />
              </Box>
            </CardActions>
          )}
            </>
          )}
        </RewardsLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100vh">
          <AccessDenied />
        </Box>
      )}

      <SortRewardsPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />

      {filterDialogOpen && (
        <FilterRewards
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          genderId={filters.genderId}
          stateId={filters.stateId}
          districtId={filters.districtId}
          rewardFrom={filters.rewardFrom}
          rewardTo={filters.rewardTo}
          ageFrom={filters.ageFrom}
          ageTo={filters.ageTo}
          area={filters.area}
          fromDate={filters.fromDate}
          toDate={filters.toDate}
          onApply={setFilters}
          genderList={masterDataList?.gender || []}
        />
      )}
    </MainLayout>
  );
}
